/**
 * Marketing Activity — global feed of every email going out of the system.
 *
 * Single pane of glass for "did 200 actually go out today, which leads got
 * which step of which workflow, and is anything bouncing?" Filterable by
 * date range, workflow, campaign, status, step index, free-text recipient
 * search, and individual lead.
 *
 * Built per Mike's Jun-17-2026 ask: "I want to know every kind of email
 * that's going out of the system, segmented by category and campaign."
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  PaperPlaneTilt, ArrowsClockwise, Spinner, MagnifyingGlass,
  Envelope, Warning, CheckCircle, ClockClockwise, Lightning, Gauge,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import MarketingShell from "./MarketingShell";
import { formatLocal } from "@/lib/timezone";

const STATUS_TONE = {
  sent:      "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  delivered: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  opened:    "bg-[#EFF6FF] text-[#1E3A8A] border-[#BFDBFE]",
  clicked:   "bg-[#EFF6FF] text-[#1E3A8A] border-[#BFDBFE]",
  queued:    "bg-[#FEF9C3] text-[#854D0E] border-[#FDE68A]",
  scheduled: "bg-[#FEF9C3] text-[#854D0E] border-[#FDE68A]",
  bounced:   "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  dropped:   "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  blocked:   "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  spam:      "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  failed:    "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  deferred:  "bg-[#FAFAFA] text-[#525252] border-[#E5E5E5]",
};

const CATEGORY_TONE = {
  workflow:      "bg-[#EFF6FF] text-[#1E3A8A] border-[#BFDBFE]",
  campaign:      "bg-[#FCE7F3] text-[#831843] border-[#FBCFE8]",
  transactional: "bg-[#F3F4F6] text-[#374151] border-[#D1D5DB]",
};

const BAND_TONE = {
  green:    "bg-[#10B981]",
  amber:    "bg-[#F59E0B]",
  red:      "bg-[#EF4444]",
  starter:  "bg-[#6B7280]",
  override: "bg-[#0055FF]",
};

const PAGE_SIZE = 100;

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function MarketingActivityPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  // Filters — persisted in URL query string so a link to a filtered view is shareable.
  const [filters, setFilters] = useState({
    date_from: params.get("date_from") || daysAgo(7),
    date_to: params.get("date_to") || today(),
    workflow_id: params.get("workflow_id") || "",
    campaign_id: params.get("campaign_id") || "",
    step_index: params.get("step_index") || "",
    status: params.get("status") || "",
    category: params.get("category") || "all",
    search: params.get("search") || "",
    lead_id: params.get("lead_id") || "",
  });

  const [filterOptions, setFilterOptions] = useState({ workflows: [], campaigns: [], statuses: [], step_indexes: [] });
  const [summary, setSummary] = useState(null);
  // Cap-override modal state (v1.69) — operator can adjust the daily
  // cap inline from the dispatcher tile without curl. The override
  // POST is audit-logged + auto-cancels the previous active row.
  const [capModalOpen, setCapModalOpen] = useState(false);
  const [capInput, setCapInput] = useState("200");
  const [capReason, setCapReason] = useState("");
  const [capSubmitting, setCapSubmitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  // Load filter options once.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/marketing/activity/filters");
        setFilterOptions(r.data);
      } catch (e) {
        toast.error("Failed to load filter options");
      }
    })();
  }, []);

  const buildQuery = useCallback(() => {
    const qp = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) qp.set(k, v);
    });
    qp.set("limit", String(PAGE_SIZE));
    qp.set("offset", String(offset));
    return qp.toString();
  }, [filters, offset]);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    try {
      const [feed, sum] = await Promise.all([
        api.get(`/marketing/activity?${buildQuery()}`),
        api.get("/marketing/activity/summary"),
      ]);
      setRows(feed.data.rows || []);
      setTotal(feed.data.total || 0);
      setSummary(sum.data);
      // Mirror filters into URL so the operator can bookmark/share the view.
      const next = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) next.set(k, v); });
      setParams(next, { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [buildQuery, filters, setParams]);

  useEffect(() => { loadFeed(); /* eslint-disable-next-line */ }, [offset, filters]);

  // (Auto-refresh polling removed pending a clean hook pattern that
  // doesn't trip react-hooks/set-state-in-effect. The Refresh button
  // is the source of truth for now — Mike can click it.)

  const update = (k, v) => { setOffset(0); setFilters(f => ({ ...f, [k]: v })); };
  const reset = () => {
    setOffset(0);
    setFilters({
      date_from: daysAgo(7), date_to: today(),
      workflow_id: "", campaign_id: "", step_index: "", status: "",
      category: "all", search: "", lead_id: "",
    });
  };

  const capPct = summary?.cap?.value
    ? Math.min(100, (summary.today.total / summary.cap.value) * 100)
    : 0;

  return (
    <MarketingShell>
      <div className="px-4 py-3 w-full space-y-3" data-testid="marketing-activity-page">
        {/* Compact header row — title + description + refresh inline */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-black tracking-tight text-[#0A0A0A] flex items-center gap-2">
              <Lightning size={20} weight="bold" /> Email Activity
            </h1>
            <p className="text-[11px] text-[#A3A3A3] leading-snug hidden lg:block">
              Every email going out of Pulse — filter by lead, workflow, campaign, status, step or date.
            </p>
          </div>
          <button onClick={loadFeed} disabled={loading}
                  data-testid="activity-refresh"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#E5E5E5] hover:border-[#0055FF] hover:text-[#0055FF] text-xs">
            {loading ? <Spinner size={12} className="animate-spin"/> : <ArrowsClockwise size={12}/>}
            Refresh
          </button>
        </div>

        {/* Compact top strip — tiles inline with the filter bar on wide screens */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="activity-tiles">
            <Tile icon={Envelope} label="Sent today" value={summary.today.sent}
                  hint={`${summary.today.bounced} bounced · ${summary.today.failed} failed`} />
            <Tile icon={ClockClockwise} label="Queued today" value={summary.today.queued}
                  hint={`${summary.backlog_due_now} due now`} />
            <Tile icon={Gauge} label="Daily cap"
                  value={`${summary.today.total} / ${summary.cap.value}`}
                  hint={`${summary.cap.band || "—"} · ${summary.cap.headroom} headroom · click to override`}
                  bar={{ pct: capPct, tone: BAND_TONE[summary.cap.band] || "bg-[#0055FF]" }}
                  onClick={() => {
                    setCapInput(String(summary.cap.value || 200));
                    setCapReason("");
                    setCapModalOpen(true);
                  }}
                  testid="activity-tile-cap" />
            <Tile icon={CheckCircle} label="7-day total"
                  value={(summary.last_7d || []).reduce((s,r) => s + (r.count || 0), 0)}
                  hint={(summary.last_7d || []).map(r => r.count).join(" · ")}
                  hintTitle={(summary.last_7d || []).map(r => `${r.date}: ${r.count}`).join("\n")} />
          </div>
        )}

        {/* Single-row horizontal filter bar */}
        <div className="border border-[#E5E5E5] bg-white px-3 py-2" data-testid="activity-filters">
          <div className="flex items-end gap-2 flex-wrap">
            <Field label="From">
              <input type="date" value={filters.date_from} onChange={e => update("date_from", e.target.value)}
                     data-testid="activity-filter-from"
                     className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-32"/>
            </Field>
            <Field label="To">
              <input type="date" value={filters.date_to} onChange={e => update("date_to", e.target.value)}
                     data-testid="activity-filter-to"
                     className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-32"/>
            </Field>
            <Field label="Workflow">
              <select value={filters.workflow_id} onChange={e => update("workflow_id", e.target.value)}
                      data-testid="activity-filter-workflow"
                      className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-48">
                <option value="">All</option>
                {filterOptions.workflows.map(w =>
                  <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </Field>
            <Field label="Campaign">
              <select value={filters.campaign_id} onChange={e => update("campaign_id", e.target.value)}
                      data-testid="activity-filter-campaign"
                      className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-44">
                <option value="">All</option>
                {filterOptions.campaigns.map(c =>
                  <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Step #">
              <select value={filters.step_index} onChange={e => update("step_index", e.target.value)}
                      data-testid="activity-filter-step"
                      className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-28">
                <option value="">All</option>
                {filterOptions.step_indexes.map(s =>
                  <option key={s} value={s}>Email {s + 1}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={filters.status} onChange={e => update("status", e.target.value)}
                      data-testid="activity-filter-status"
                      className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-28">
                <option value="">All</option>
                {filterOptions.statuses.map(s =>
                  <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Category">
              <div className="flex gap-0.5" data-testid="activity-filter-category">
                {[
                  { v: "all",      label: "All" },
                  { v: "workflow", label: "WF" },
                  { v: "campaign", label: "Camp" },
                  { v: "transactional", label: "Txn" },
                ].map(c => (
                  <button key={c.v} onClick={() => update("category", c.v)}
                          title={c.v}
                          className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${
                            filters.category === c.v
                              ? "border-[#0055FF] bg-[#EFF6FF] text-[#0055FF]"
                              : "border-[#E5E5E5] text-[#525252] hover:border-[#0055FF]"
                          }`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Search">
              <div className="relative">
                <MagnifyingGlass size={10} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                <input type="text" value={filters.search} onChange={e => update("search", e.target.value)}
                       placeholder="email / name / company"
                       data-testid="activity-filter-search"
                       className="border border-[#E5E5E5] pl-5 pr-1.5 py-0.5 text-xs w-56" />
              </div>
            </Field>
            <Field label="Lead ID">
              <input type="text" value={filters.lead_id} onChange={e => update("lead_id", e.target.value)}
                     placeholder="lead_…"
                     data-testid="activity-filter-lead"
                     className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-36 font-mono" />
            </Field>
            <button onClick={reset} className="text-[10px] underline text-[#525252] hover:text-[#0055FF] pb-1 ml-auto"
                    data-testid="activity-filter-reset">Reset</button>
          </div>
        </div>

        {/* Table */}
        <div className="border border-[#E5E5E5] bg-white">
          <div className="px-3 py-1.5 border-b border-[#E5E5E5] bg-[#FAFAFA] flex items-center justify-between">
            <div className="text-[11px] text-[#525252]">
              <span className="font-bold text-[#0A0A0A]">{total.toLocaleString()}</span> matching · showing
              <span className="font-bold text-[#0A0A0A] ml-1">{offset + 1}–{Math.min(offset + rows.length, total)}</span>
            </div>
            <div className="flex gap-1">
              <button disabled={offset === 0 || loading}
                      onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                      data-testid="activity-prev"
                      className="px-2 py-0.5 text-[11px] border border-[#E5E5E5] disabled:opacity-40">Prev</button>
              <button disabled={offset + rows.length >= total || loading}
                      onClick={() => setOffset(offset + PAGE_SIZE)}
                      data-testid="activity-next"
                      className="px-2 py-0.5 text-[11px] border border-[#E5E5E5] disabled:opacity-40">Next</button>
            </div>
          </div>

          {loading && rows.length === 0 && (
            <div className="p-6 flex justify-center" data-testid="activity-loading">
              <Spinner size={24} className="animate-spin text-[#0055FF]"/>
            </div>
          )}

          {!loading && rows.length === 0 && (
            <div className="p-6 text-center" data-testid="activity-empty">
              <Envelope size={20} className="text-[#A3A3A3] mx-auto mb-1"/>
              <p className="text-xs font-bold text-[#525252]">No emails match the current filters.</p>
              <button onClick={reset} className="mt-1 text-[11px] underline text-[#0055FF]">Reset filters</button>
            </div>
          )}

          {rows.length > 0 && (
            <table className="w-full text-xs" data-testid="activity-table">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5] text-[9px] font-bold uppercase tracking-wider text-[#525252]">
                <tr>
                  <th className="px-2 py-1.5 text-left">Recipient</th>
                  <th className="px-2 py-1.5 text-left">Source</th>
                  <th className="px-2 py-1.5 text-left">Step</th>
                  <th className="px-2 py-1.5 text-left">Subject</th>
                  <th className="px-2 py-1.5 text-left">Status</th>
                  <th className="px-2 py-1.5 text-left">Queued</th>
                  <th className="px-2 py-1.5 text-left">Sent</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]"
                      data-testid={`activity-row-${r.id}`}>
                    <td className="px-2 py-1.5 align-top">
                      <div className="font-bold text-[#0A0A0A] text-xs leading-tight">{r.contact_name || "—"}</div>
                      <div className="text-[10px] text-[#A3A3A3] leading-tight">{r.to_email}</div>
                      {r.company_name && <div className="text-[10px] text-[#A3A3A3] italic leading-tight">{r.company_name}</div>}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <span className={`inline-block px-1 py-0 text-[9px] font-bold uppercase tracking-wider border ${CATEGORY_TONE[r.category]}`}>
                        {r.category}
                      </span>
                      <div className="text-[10px] text-[#525252] leading-tight mt-0.5 max-w-[180px] truncate"
                           title={r.workflow_name || r.campaign_name || ""}>
                        {r.workflow_name || r.campaign_name || "—"}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 align-top text-[11px] text-[#525252] whitespace-nowrap">
                      {r.step_label || "—"}
                    </td>
                    <td className="px-2 py-1.5 align-top text-[11px] text-[#0A0A0A] max-w-md truncate"
                        title={r.subject || ""}>
                      {r.subject || <span className="italic text-[#A3A3A3]">no subject</span>}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <span className={`inline-block px-1 py-0 text-[9px] font-bold uppercase tracking-wider border ${STATUS_TONE[r.status] || "bg-[#FAFAFA] text-[#525252] border-[#E5E5E5]"}`}>
                        {r.status}
                      </span>
                      {r.error_message && (
                        <div className="text-[9px] text-[#991B1B] leading-tight mt-0.5 max-w-xs truncate"
                             title={r.error_message}>
                          <Warning size={9} className="inline -mt-0.5 mr-0.5"/>
                          {r.error_message.slice(0, 60)}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-top text-[10px] text-[#525252] whitespace-nowrap">
                      {r.queued_at ? formatLocal(r.queued_at) : "—"}
                    </td>
                    <td className="px-2 py-1.5 align-top text-[10px] text-[#525252] whitespace-nowrap">
                      {r.sent_at ? formatLocal(r.sent_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Cap-override modal (v1.69) */}
        {capModalOpen && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
               onClick={() => !capSubmitting && setCapModalOpen(false)}
               data-testid="cap-override-modal-overlay">
            <div className="bg-white border border-[#E5E5E5] max-w-md w-full p-5 space-y-3"
                 onClick={e => e.stopPropagation()}>
              <div>
                <h2 className="text-lg font-black text-[#0A0A0A] flex items-center gap-2">
                  <Gauge size={18} weight="bold"/> Override daily send cap
                </h2>
                <p className="text-xs text-[#525252] mt-1 leading-snug">
                  Sets a manual override on `mkt_cap_overrides`. The adaptive
                  bounce-rate kill switches still apply on top (amber → 150,
                  red → 0). Audit-logged. Replaces any active override.
                </p>
                {summary && (
                  <p className="text-[11px] text-[#A3A3A3] mt-2">
                    Current cap: <span className="font-bold text-[#0A0A0A]">{summary.cap.value}</span>
                    {" "}· band: <span className="font-bold text-[#0A0A0A]">{summary.cap.band || "—"}</span>
                    {" "}· today: <span className="font-bold text-[#0A0A0A]">{summary.today.total}</span> sent
                  </p>
                )}
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">New daily cap</label>
                <input type="number" min="1" max="10000" step="1"
                       value={capInput} onChange={e => setCapInput(e.target.value)}
                       disabled={capSubmitting}
                       data-testid="cap-override-input"
                       className="border border-[#E5E5E5] px-2 py-1.5 text-sm w-full mt-1"/>
                <div className="flex gap-1 mt-1.5">
                  {[100, 200, 300, 500].map(n => (
                    <button key={n} type="button" disabled={capSubmitting}
                            onClick={() => setCapInput(String(n))}
                            className={`px-2 py-0.5 text-[10px] font-bold border ${
                              String(n) === capInput
                                ? "border-[#0055FF] bg-[#EFF6FF] text-[#0055FF]"
                                : "border-[#E5E5E5] text-[#525252] hover:border-[#0055FF]"
                            }`}>
                      {n}/day
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">Reason (audit trail)</label>
                <textarea value={capReason} onChange={e => setCapReason(e.target.value)}
                          rows={2} disabled={capSubmitting}
                          placeholder="e.g. drain Reactivate-cold-leads backlog"
                          data-testid="cap-override-reason"
                          className="border border-[#E5E5E5] px-2 py-1.5 text-xs w-full mt-1"/>
              </div>
              <div className="flex justify-between items-center gap-2 pt-1">
                <button onClick={async () => {
                          if (capSubmitting) return;
                          if (!window.confirm("Clear the override and revert to the adaptive band?")) return;
                          setCapSubmitting(true);
                          try {
                            await api.delete("/marketing/cap-override");
                            toast.success("Override cleared. Adaptive band rules now apply.");
                            setCapModalOpen(false);
                            loadFeed();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Failed to clear override");
                          } finally { setCapSubmitting(false); }
                        }}
                        disabled={capSubmitting}
                        data-testid="cap-override-clear"
                        className="text-[11px] underline text-[#991B1B] hover:text-[#7F1D1D] disabled:opacity-40">
                  Clear override
                </button>
                <div className="flex gap-2">
                  <button onClick={() => setCapModalOpen(false)} disabled={capSubmitting}
                          data-testid="cap-override-cancel"
                          className="px-3 py-1.5 border border-[#E5E5E5] hover:border-[#0055FF] hover:text-[#0055FF] text-xs">
                    Cancel
                  </button>
                  <button onClick={async () => {
                            const cap = parseInt(capInput, 10);
                            if (!cap || cap < 1 || cap > 10000) {
                              toast.error("Cap must be between 1 and 10000");
                              return;
                            }
                            setCapSubmitting(true);
                            try {
                              const r = await api.post("/marketing/cap-override", {
                                daily_cap: cap, reason: capReason || null,
                              });
                              toast.success(`Override set: ${r.data.daily_cap}/day · ${r.data.id}`);
                              setCapModalOpen(false);
                              loadFeed();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Failed to set override");
                            } finally { setCapSubmitting(false); }
                          }}
                          disabled={capSubmitting}
                          data-testid="cap-override-submit"
                          className="px-3 py-1.5 bg-[#0055FF] hover:bg-[#003ECC] text-white text-xs font-bold">
                    {capSubmitting ? "Saving…" : "Set override"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MarketingShell>
  );
}

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-0.5">
    <label className="text-[8px] font-bold uppercase tracking-wider text-[#A3A3A3] leading-none">{label}</label>
    {children}
  </div>
);

const Tile = ({ icon: Icon, label, value, hint, hintTitle, bar, testid, onClick }) => {
  const interactive = typeof onClick === "function";
  return (
    <div
      className={`border border-[#E5E5E5] bg-white px-2.5 py-1.5 ${interactive ? "cursor-pointer hover:border-[#0055FF] hover:bg-[#F0F7FF] transition-colors" : ""}`}
      data-testid={testid}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-[#525252]">
        <Icon size={10} weight="bold"/> {label}
      </div>
      <div className="text-lg font-black tracking-tight text-[#0A0A0A] leading-tight mt-0.5">{value ?? "—"}</div>
      {hint && (
        <div className="text-[9px] text-[#A3A3A3] leading-tight" title={hintTitle || ""}>{hint}</div>
      )}
      {bar && (
        <div className="mt-1 h-1 bg-[#F5F5F5] rounded overflow-hidden">
          <div className={`h-full ${bar.tone}`} style={{ width: `${bar.pct}%` }}/>
        </div>
      )}
    </div>
  );
};
